import * as Sentry from "@sentry/browser";
import { BrowserTracing } from "@sentry/tracing";


// The all-caps constants in this file get subbed in during
// compilation. Search for them in the gulpfile to see how they're
// determined.
//
// Runtime origin URLs come from window.MC_ENV (populated by
// /config.js, which is maintained per-environment by dev ops). See
// config.js.sample for the shape.

// Sample tracing more aggressively in lower envs and lighter in
// production where traffic volume is high.
const TRACES_SAMPLE_RATES = {
  production: 0.1,
  staging: 0.5,
  performance: 1.0,
  security: 0.5,
  development: 1.0
};

const expandRootDomain = (url) => {
  const rootDomain = (window.MC_ENV && window.MC_ENV.root_domain) || "";
  return url.replace("{root_domain}", rootDomain);
};

const buildTracingOrigins = () => {
  try {
    const origins = (window.MC_ENV && window.MC_ENV.origins) || {};
    const urls = Object.values(origins)
      .filter(Boolean)
      .map(expandRootDomain)
      .map((url) => url.replace(/^https?:\/\//, "")) // tracingOrigins matches substrings; hostname is enough
      .filter((url) => url && !url.includes("{"));   // drop anything with unresolved placeholders

    // Always keep same-origin tracing on; dedupe.
    return Array.from(new Set([...urls, /^\//]));
  } catch (e) {
    // Any failure in env resolution degrades to same-origin tracing
    // rather than blocking init. Log it so the misconfiguration is visible.
    console.warn("Sentry: failed to resolve tracingOrigins, falling back to same-origin only:", e);
    return [/^\//];
  }
};

// Sentry init must never throw out of this function. A failure here
// would cascade through index.js and prevent the rest of the app
// (i18n, fullstory, BubblesheetCore, the React mount) from running.
// We expose window.Sentry first so callers in App.js can still invoke
// no-op setContext/setUser even if init below fails.
export default () => {
  window.Sentry = Sentry;

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      integrations: [
        new BrowserTracing({
          tracingOrigins: buildTracingOrigins()
        })
      ],
      tracesSampleRate: TRACES_SAMPLE_RATES[BUILT_FOR_ENV] != null
        ? TRACES_SAMPLE_RATES[BUILT_FOR_ENV]
        : 0.2,
      release: PACKAGE_VERSION,
      environment: BUILT_FOR_ENV
    });
  } catch (e) {
    console.error("Sentry init failed; continuing without it:", e);
  }
};